/* eslint-disable react/prop-types */
import React, { useEffect, useMemo, useState } from "react";
import { FiSearch } from "react-icons/fi";
import { debounce } from "lodash";
import { searchMedicines } from "../api";

/**
 * Resolve one line against the catalogue.
 *
 * OCR usually returns ranked suggestions for a line it couldn't pin down — a
 * strength it couldn't read, a brand with four variants — so those come first
 * and search is the fallback, not the entry point.
 */
const MedicinePicker = ({ suggestions = [], onPick, onCancel }) => {
    const [query, setQuery] = useState("");
    const [results, setResults] = useState([]);
    const [isSearching, setIsSearching] = useState(false);

    const run = useMemo(
        () =>
            debounce(async (text) => {
                if (text.trim().length < 3) {
                    setResults([]);
                    setIsSearching(false);
                    return;
                }

                try {
                    const found = await searchMedicines(text.trim());
                    setResults(Array.isArray(found) ? found.slice(0, 8) : []);
                } catch {
                    setResults([]);
                } finally {
                    setIsSearching(false);
                }
            }, 350),
        []
    );

    useEffect(() => () => run.cancel(), [run]);

    const options = query.trim().length >= 3 ? results : suggestions;
    const isShowingSearch = query.trim().length >= 3;

    return (
        <div className="mt-3 rounded-lg border border-cod-gray-100 bg-cod-gray-50 p-3">
            <div className="relative">
                <FiSearch className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-cod-gray-300" size={15} />
                <input
                    type="text"
                    value={query}
                    autoFocus
                    onChange={(e) => {
                        setQuery(e.target.value);
                        setIsSearching(true);
                        run(e.target.value);
                    }}
                    placeholder="Search the catalogue"
                    className="w-full rounded-lg border-cod-gray-200 bg-white-50 py-2 pl-9 pr-3 text-sm placeholder:text-cod-gray-300 focus:border-elm-400 focus:ring-elm-400"
                />
            </div>

            {!isShowingSearch && suggestions.length > 0 && (
                <p className="mt-3 text-[11px] font-semibold uppercase tracking-[0.1em] text-cod-gray-400">
                    Closest matches
                </p>
            )}

            {isSearching && isShowingSearch && (
                <p className="mt-3 text-xs text-cod-gray-400">Searching…</p>
            )}

            {!isSearching && isShowingSearch && options.length === 0 && (
                <p className="mt-3 text-xs text-cod-gray-400">
                    Nothing matched “{query.trim()}”. Try the brand name on its own.
                </p>
            )}

            {options.length > 0 && (
                <ul className="mt-2 flex max-h-56 flex-col gap-1 overflow-y-auto">
                    {options.map((option, index) => (
                        <li key={option.medicineId || option.medicine_id || `${option.name}-${index}`}>
                            <button
                                type="button"
                                onClick={() => onPick(option)}
                                className="w-full rounded-lg border border-transparent bg-white-50 px-3 py-2.5 text-left transition-colors hover:border-elm-300 hover:bg-elm-50"
                            >
                                <span className="block text-sm font-semibold text-cod-gray-900">{option.name}</span>
                                {(option.manufacturer || option.packing) && (
                                    <span className="mt-0.5 block text-[11px] text-cod-gray-400">
                                        {[option.manufacturer, option.packing].filter(Boolean).join(" · ")}
                                    </span>
                                )}
                            </button>
                        </li>
                    ))}
                </ul>
            )}

            {onCancel && (
                <button
                    type="button"
                    onClick={onCancel}
                    className="mt-3 text-xs font-semibold text-cod-gray-400 hover:text-cod-gray-900"
                >
                    Cancel
                </button>
            )}
        </div>
    );
};

export default MedicinePicker;
